import { useState, useEffect } from 'react';
import { authFetch } from '../../lib/rpc';
import { reportDataFetchError } from '../../lib/telemetry';
import { TranscriptionStatus, TranscriptionTarget } from './cdrTypes';

interface UseTranscriptionOptions {
  audioDownloadUrlPrefix: string;
  serverId: string;
  crm?: any;
}

export function useTranscription(
  optionsOrServerId: UseTranscriptionOptions | string,
  maybeAudioPrefix?: string,
  maybeCrm?: any
) {
  const options: UseTranscriptionOptions = typeof optionsOrServerId === 'string'
    ? { serverId: optionsOrServerId, audioDownloadUrlPrefix: maybeAudioPrefix || '', crm: maybeCrm }
    : optionsOrServerId;
  const { audioDownloadUrlPrefix, serverId, crm } = options;
  const [transcriptionModalOpen, setTranscriptionModalOpen] = useState(false);
  const [transcriptionTargetFile, setTranscriptionTargetFile] = useState<TranscriptionTarget | null>(null);
  const [transcriptionStatus, setTranscriptionStatus] = useState<TranscriptionStatus>(null);
  const [transcriptionText, setTranscriptionText] = useState<string>('');
  const [transcriptionError, setTranscriptionError] = useState<string>('');
  const [transcriptionExistsMap, setTranscriptionExistsMap] = useState<Record<string, boolean>>({});

  const triggerTranscription = async (r2Key: string, method: 'primary' | 'secondary') => {
    setTranscriptionStatus('pending');
    setTranscriptionError('');
    try {
      const match = audioDownloadUrlPrefix.match(/\/api\/v1\/crm-dashboard\/([^/]+)/);
      if (!match) throw new Error("Could not determine dashboard slug");
      const slug = match[1];

      const parsedUrl = new URL(audioDownloadUrlPrefix, window.location.origin);
      const crmPw = (typeof window !== 'undefined' ? localStorage.getItem(`crm-pw-${slug}`) : '') || parsedUrl.searchParams.get('x-crm-password') || '';
      const cdrPw = (typeof window !== 'undefined' ? (sessionStorage.getItem(`crm-cdr-pw-${slug}`) || localStorage.getItem(`crm-cdr-pw-${slug}`)) : '') || parsedUrl.searchParams.get('x-crm-cdr-password') || '';

      const res = await authFetch(`${parsedUrl.origin}/api/v1/crm-dashboard/${slug}/transcribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-crm-password': crmPw,
          'x-crm-cdr-password': cdrPw
        },
        body: JSON.stringify({ r2Key: `pbx-${serverId}/${r2Key}`, method })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to trigger transcription');
      }
    } catch (e: any) {
      reportDataFetchError('crm', 'Transcription', e, { action: 'triggerTranscription', serverId, r2Key });
      setTranscriptionError(e.message);
      setTranscriptionStatus('failed');
    }
  };

  const openTranscriptionModal = async (filename: string, r2Key: string, url: string, txtUrl: string) => {
    setTranscriptionTargetFile({ filename, r2Key, url, txtUrl });
    setTranscriptionStatus('loading');
    setTranscriptionText('');
    setTranscriptionError('');
    setTranscriptionModalOpen(true);

    try {
      let res = await authFetch(txtUrl + '&t=' + Date.now());
      if (!res.ok && txtUrl.includes('+')) {
        res = await authFetch(txtUrl.replace(/\+/g, '_') + '&t=' + Date.now());
      }
      if (res.ok) {
        const text = await res.text();
        setTranscriptionText(text);
        setTranscriptionStatus('completed');
      } else {
        if (crm?.serverConfig?.transcriptionEngineCallsPrimary || crm?.serverConfig?.transcriptionEngineCallsSecondary) {
          setTranscriptionStatus('select_engine');
        } else {
          setTranscriptionError('Transcription engine is not configured on this server.');
          setTranscriptionStatus('failed');
        }
      }
    } catch (e: any) {
      reportDataFetchError('crm', 'Transcription', e, { action: 'openTranscriptionModal', filename, r2Key });
      setTranscriptionError(e.message);
      setTranscriptionStatus('failed');
    }
  };

  useEffect(() => {
    let interval: any;
    if (transcriptionModalOpen && transcriptionTargetFile && (transcriptionStatus === 'pending' || transcriptionStatus === 'processing')) {
      interval = setInterval(async () => {
        try {
          const match = audioDownloadUrlPrefix.match(/\/api\/v1\/crm-dashboard\/([^/]+)/);
          if (!match) return;
          const slug = match[1];

          const parsedUrl = new URL(audioDownloadUrlPrefix, window.location.origin);
          const crmPw = (typeof window !== 'undefined' ? localStorage.getItem(`crm-pw-${slug}`) : '') || parsedUrl.searchParams.get('x-crm-password') || '';
          const cdrPw = (typeof window !== 'undefined' ? (sessionStorage.getItem(`crm-cdr-pw-${slug}`) || localStorage.getItem(`crm-cdr-pw-${slug}`)) : '') || parsedUrl.searchParams.get('x-crm-cdr-password') || '';

          const res = await authFetch(`${parsedUrl.origin}/api/v1/crm-dashboard/${slug}/transcribe/status?key=${encodeURIComponent(`pbx-${serverId}/${transcriptionTargetFile.r2Key}`)}`, {
            headers: {
              'x-crm-password': crmPw,
              'x-crm-cdr-password': cdrPw
            }
          });
          const data = await res.json();
          if (data.success) {
            setTranscriptionStatus(data.status);
            if (data.status === 'completed') {
              let textRes = await authFetch(transcriptionTargetFile.txtUrl + '&t=' + Date.now());
              if (!textRes.ok && transcriptionTargetFile.txtUrl.includes('+')) {
                textRes = await authFetch(transcriptionTargetFile.txtUrl.replace(/\+/g, '_') + '&t=' + Date.now());
              }
              if (textRes.ok) {
                setTranscriptionText(await textRes.text());
              } else {
                setTranscriptionStatus('failed');
                setTranscriptionError('Transcription finished, but the transcript file could not be loaded.');
              }
            } else if (data.status === 'failed') {
              setTranscriptionError('Transcription job failed.');
            }
          }
        } catch (e: any) {
          reportDataFetchError('crm', 'Transcription', e, { action: 'pollStatus', serverId, r2Key: transcriptionTargetFile?.r2Key });
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [transcriptionModalOpen, transcriptionTargetFile, transcriptionStatus, audioDownloadUrlPrefix, serverId]);

  return {
    transcriptionModalOpen,
    setTranscriptionModalOpen,
    transcriptionTargetFile,
    transcriptionStatus,
    transcriptionText,
    transcriptionError,
    transcriptionExistsMap,
    setTranscriptionExistsMap,
    triggerTranscription,
    openTranscriptionModal
  };
}
